import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

// ─── Seeded pseudo-random helpers ───────────────────────────────────────────
const buildSeed = (value = '') =>
  Array.from(String(value)).reduce((acc, ch) => ((acc * 31) + ch.charCodeAt(0)) % 100000, 17);

const seededRange = (seed, min, max) => {
  const normalized = (seed % 1000) / 1000;
  return min + (max - min) * normalized;
};

const buildAthleteProfile = () => {
  const seed = buildSeed('athlete-demo-slam');
  return {
    name: 'Marcus Webb',
    position: 'PG',
    number: '3',
    readiness: Math.round(seededRange(seed + 11, 72, 94)),
    sleepHours: seededRange(seed + 27, 6.2, 8.8),
    hrv: Math.round(seededRange(seed + 43, 48, 82)),
    strain: seededRange(seed + 71, 8, 16),
    bodyBattery: Math.round(seededRange(seed + 89, 45, 92)),
    restingHr: Math.round(seededRange(seed + 107, 50, 65)),
    whoopConnected: true,
    ouraConnected: seed % 5 !== 0,
    catapultConnected: seed % 6 !== 0,
    // 7-day trend (readiness scores)
    trend: Array.from({ length: 7 }, (_, i) => Math.round(seededRange(buildSeed(`trend-${i}`), 62, 96))),
    trendLabels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  };
};

// ─── Ring component (SVG arc) ────────────────────────────────────────────────
const Ring = ({ value, max = 100, size = 88, stroke = 7, color = '#E8B800', bgColor = '#1F1F1F', children }) => {
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const filled = (value / max) * circumference;
  const cx = size / 2;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="rotate-[-90deg]">
      <circle cx={cx} cy={cx} r={r} fill="none" stroke={bgColor} strokeWidth={stroke} />
      <circle
        cx={cx} cy={cx} r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeDasharray={`${filled} ${circumference - filled}`}
        strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 0.8s ease' }}
      />
      {children}
    </svg>
  );
};

// ─── Small sub-metric ring ───────────────────────────────────────────────────
const SubRing = ({ label, value, unit, max = 100, color = '#E8B800' }) => (
  <div className="flex flex-col items-center gap-1">
    <div className="relative w-[60px] h-[60px]">
      <Ring value={value} max={max} size={60} stroke={5} color={color} bgColor="#1A1A1A" />
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-[11px] font-bold text-white">{Math.round(value)}</span>
      </div>
    </div>
    <p className="text-[9px] text-zinc-500 uppercase tracking-widest text-center">{label}</p>
    {unit && <p className="text-[9px] text-zinc-600 -mt-1">{unit}</p>}
  </div>
);

// ─── Metric bar ──────────────────────────────────────────────────────────────
const MetricBar = ({ label, value, max = 100, unit = '', warn = false }) => {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  const barColor = warn ? '#ef4444' : '#E8B800';
  return (
    <div className="space-y-1">
      <div className="flex justify-between items-baseline">
        <span className="text-[10px] text-zinc-400">{label}</span>
        <span className="text-[10px] font-semibold text-white">{typeof value === 'number' && !Number.isInteger(value) ? value.toFixed(1) : value}{unit}</span>
      </div>
      <div className="h-[3px] rounded-full bg-[#1A1A1A] overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{ width: `${pct}%`, backgroundColor: barColor, transition: 'width 0.8s ease' }}
        />
      </div>
    </div>
  );
};

// ─── Mini trend sparkline ────────────────────────────────────────────────────
const TrendLine = ({ data, labels }) => {
  const W = 260;
  const H = 40;
  const min = Math.min(...data) - 5;
  const max = Math.max(...data) + 5;
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * (W - 20) + 10;
    const y = H - ((v - min) / range) * (H - 8) - 4;
    return `${x},${y}`;
  });
  const d = `M ${pts.join(' L ')}`;
  const todayX = parseFloat(pts[pts.length - 1].split(',')[0]);
  const todayY = parseFloat(pts[pts.length - 1].split(',')[1]);

  return (
    <div className="mt-1">
      <svg width={W} height={H + 14} viewBox={`0 0 ${W} ${H + 14}`} className="w-full">
        {/* Grid lines */}
        {[0, 1, 2].map(i => (
          <line key={i} x1="10" x2={W - 10} y1={4 + i * (H / 2)} y2={4 + i * (H / 2)}
            stroke="#1F1F1F" strokeWidth="1" />
        ))}
        {/* Area fill */}
        <defs>
          <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#E8B800" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#E8B800" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d={`${d} L ${pts[pts.length - 1].split(',')[0]},${H} L 10,${H} Z`}
          fill="url(#trendGrad)"
        />
        {/* Main line */}
        <path d={d} fill="none" stroke="#E8B800" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        {/* Today dot */}
        <circle cx={todayX} cy={todayY} r="3.5" fill="#E8B800" />
        {/* Day labels */}
        {labels.map((l, i) => {
          const x = (i / (labels.length - 1)) * (W - 20) + 10;
          return (
            <text key={i} x={x} y={H + 12} textAnchor="middle" fontSize="8" fill="#555" fontFamily="sans-serif">
              {l}
            </text>
          );
        })}
      </svg>
    </div>
  );
};

// ─── Status color helpers ────────────────────────────────────────────────────
const readinessStatus = (v) => {
  if (v >= 80) return { label: 'Peak', color: '#22c55e' };
  if (v >= 65) return { label: 'Good', color: '#E8B800' };
  return { label: 'Low', color: '#ef4444' };
};

// ─── Main page ───────────────────────────────────────────────────────────────
export default function BiometricsPage() {
  const location = useLocation();
  const isMobile = useMemo(() => {
    const p = new URLSearchParams(location.search);
    return p.get('mobileViewport') === '1' || p.get('mobileEmulator') === '1';
  }, [location.search]);

  const p = useMemo(() => buildAthleteProfile(), []);
  const status = readinessStatus(p.readiness);
  const connectedCount = [p.whoopConnected, p.ouraConnected, p.catapultConnected].filter(Boolean).length;

  return (
    <div className={`min-h-full bg-black text-white ${isMobile ? 'p-3 pb-16' : 'p-5 max-w-md mx-auto'}`}>

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-600">Biometrics</p>
          <h1 className="text-[15px] font-semibold tracking-tight">{p.name}</h1>
          <p className="text-[10px] text-zinc-500">{p.position} · #{p.number} · {connectedCount}/3 devices</p>
        </div>
        <div
          className="text-[9px] font-bold uppercase tracking-widest px-2 py-1 rounded-full border"
          style={{ color: status.color, borderColor: `${status.color}40`, backgroundColor: `${status.color}10` }}
        >
          {status.label}
        </div>
      </div>

      {/* Main readiness ring */}
      <div className="flex flex-col items-center mb-5">
        <div className="relative w-[120px] h-[120px]">
          <Ring value={p.readiness} size={120} stroke={10} color={status.color}>
            {/* inner content is absolutely positioned below */}
          </Ring>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-[28px] font-bold leading-none" style={{ color: status.color }}>{p.readiness}</span>
            <span className="text-[9px] text-zinc-500 uppercase tracking-widest mt-0.5">Readiness</span>
          </div>
        </div>
        <p className="text-[10px] text-zinc-500 mt-2">
          {p.readiness >= 80 ? 'You\'re ready to perform at a high level.' : p.readiness >= 65 ? 'Moderate readiness — stay hydrated and warmed up.' : 'Low readiness — consider a recovery session.'}
        </p>
      </div>

      {/* Sub-metric rings */}
      <div className="grid grid-cols-3 gap-2 mb-5">
        <SubRing label="HRV" value={p.hrv} max={100} unit="ms" color="#3b82f6" />
        <SubRing label="Sleep" value={Math.round(p.sleepHours * 10)} max={100} unit="hrs" color="#a855f7" />
        <SubRing label="Battery" value={p.bodyBattery} max={100} color="#22c55e" />
      </div>

      {/* Metric bars */}
      <div className="rounded-2xl border border-[#1F1F1F] bg-[#0A0A0A] px-4 py-3 space-y-3 mb-4">
        <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-600 mb-2">Load Metrics</p>
        <MetricBar label="Strain" value={p.strain} max={21} unit="" warn={p.strain > 17} />
        <MetricBar label="Resting HR" value={p.restingHr} max={80} unit=" bpm" warn={p.restingHr > 65} />
        <MetricBar label="Sleep" value={p.sleepHours} max={10} unit="h" />
        <MetricBar label="Body Battery" value={p.bodyBattery} max={100} unit="%" />
      </div>

      {/* 7-day trend */}
      <div className="rounded-2xl border border-[#1F1F1F] bg-[#0A0A0A] px-4 py-3 mb-4">
        <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-600 mb-1">7-Day Readiness</p>
        <TrendLine data={p.trend} labels={p.trendLabels} />
        <div className="flex justify-between text-[9px] text-zinc-600 mt-1">
          <span>Low: {Math.min(...p.trend)}</span>
          <span>Avg: {Math.round(p.trend.reduce((a, b) => a + b, 0) / p.trend.length)}</span>
          <span>High: {Math.max(...p.trend)}</span>
        </div>
      </div>

      {/* Connected devices */}
      <div className="rounded-2xl border border-[#1F1F1F] bg-[#0A0A0A] px-4 py-3">
        <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-600 mb-3">Connected Devices</p>
        <div className="grid grid-cols-3 gap-2">
          {[
            { key: 'whoopConnected', label: 'WHOOP' },
            { key: 'ouraConnected', label: 'Oura' },
            { key: 'catapultConnected', label: 'Catapult' },
          ].map(({ key, label }) => {
            const on = p[key];
            return (
              <div
                key={key}
                className="rounded-xl border px-2 py-2 text-center"
                style={{
                  borderColor: on ? '#E8B80040' : '#1F1F1F',
                  backgroundColor: on ? '#E8B8000D' : 'transparent',
                }}
              >
                <div className="w-1.5 h-1.5 rounded-full mx-auto mb-1.5" style={{ backgroundColor: on ? '#E8B800' : '#333' }} />
                <p className="text-[10px] font-medium" style={{ color: on ? '#E8B800' : '#555' }}>{label}</p>
                <p className="text-[9px] text-zinc-600">{on ? 'Live' : 'Off'}</p>
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-[9px] text-zinc-700">Demo signals — connect your wearable for live data.</p>
      </div>
    </div>
  );
}
